export interface IScheduleSidebarDevice {
    id: number
    name: string
    type?: string
    selected?: boolean
}

export interface IScheduleDeviceDelta {
    addDigitalSignageIds: number[]
    removeDigitalSignageIds: number[]
    addGroupIds: number[]
    removeGroupIds: number[]
}

export const emptyScheduleDeviceDelta = (): IScheduleDeviceDelta => ({
    addDigitalSignageIds: [],
    removeDigitalSignageIds: [],
    addGroupIds: [],
    removeGroupIds: [],
})

const addUnique = (list: number[], id: number) => (list.includes(id) ? list : [...list, id])
const withoutId = (list: number[], id: number) => list.filter((itemId) => itemId !== id)

export const isGroupDevice = (item: { type?: string }) => item.type === 'group'

/** Добавить / снять устройство или группу в delta. Для группы countDiff всегда 0. */
export const toggleScheduleDevice = (
    delta: IScheduleDeviceDelta,
    item: { id: number; type?: string; selected?: boolean },
    selected: boolean
): { delta: IScheduleDeviceDelta; countDiff: number } => {
    const isGroup = isGroupDevice(item)
    const addKey = isGroup ? 'addGroupIds' : 'addDigitalSignageIds'
    const removeKey = isGroup ? 'removeGroupIds' : 'removeDigitalSignageIds'
    const wasOnSlot = Boolean(item.selected)
    const next = { ...delta, [addKey]: [...delta[addKey]], [removeKey]: [...delta[removeKey]] }

    if (selected) {
        next[removeKey] = withoutId(next[removeKey], item.id)
        if (!wasOnSlot) {
            next[addKey] = addUnique(next[addKey], item.id)
        }
        return { delta: next, countDiff: isGroup ? 0 : 1 }
    }

    next[addKey] = withoutId(next[addKey], item.id)
    if (wasOnSlot) {
        next[removeKey] = addUnique(next[removeKey], item.id)
    }
    return { delta: next, countDiff: isGroup ? 0 : -1 }
}

/** Убрать device id из add/remove — их уже покрывает addGroupIds. */
export const stripDevicesCoveredByGroup = (delta: IScheduleDeviceDelta, deviceIds: number[]): IScheduleDeviceDelta => ({
    ...delta,
    addDigitalSignageIds: delta.addDigitalSignageIds.filter((id) => !deviceIds.includes(id)),
    removeDigitalSignageIds: delta.removeDigitalSignageIds.filter((id) => !deviceIds.includes(id)),
})

/** Сняли одно устройство из выбранной группы → группа распадается на отдельные device id. */
export const splitGroupToDevicesExcept = (
    delta: IScheduleDeviceDelta,
    groupId: number,
    exceptDeviceId: number,
    groupDeviceIds: number[],
    isDeviceAlreadyOnSchedule: (id: number) => boolean
): IScheduleDeviceDelta => {
    let next: IScheduleDeviceDelta = {
        ...delta,
        addGroupIds: withoutId(delta.addGroupIds, groupId),
        removeGroupIds: withoutId(delta.removeGroupIds, groupId),
    }

    groupDeviceIds.forEach((id) => {
        if (id === exceptDeviceId) {
            next = { ...next, addDigitalSignageIds: withoutId(next.addDigitalSignageIds, id) }
            if (isDeviceAlreadyOnSchedule(id)) {
                next = {
                    ...next,
                    removeDigitalSignageIds: addUnique(next.removeDigitalSignageIds, id),
                }
            }
            return
        }

        next = { ...next, removeDigitalSignageIds: withoutId(next.removeDigitalSignageIds, id) }
        if (!isDeviceAlreadyOnSchedule(id)) {
            next = { ...next, addDigitalSignageIds: addUnique(next.addDigitalSignageIds, id) }
        }
    })

    return next
}

/** Поля delta для updateSchedule (пустые массивы тоже нужны бэку). */
export const toDeltaPayload = (delta: IScheduleDeviceDelta) => ({
    addDigitalSignageIds: delta.addDigitalSignageIds,
    removeDigitalSignageIds: delta.removeDigitalSignageIds,
    addGroupIds: delta.addGroupIds,
    removeGroupIds: delta.removeGroupIds,
})

/** Опции каталога: галочки устройств/групп + UI-галочки устройств внутри выбранных групп. */
export const toScheduleCatalogOptions = (
    scheduleId: number | undefined,
    delta: IScheduleDeviceDelta,
    groupUiDeviceIds: number[] = []
) => {
    const toItems = (ids: number[], type: 'digitalSignage' | 'group') => ids.map((id) => ({ id, type }))

    return {
        useScheduleCatalog: true,
        scheduleId,
        addedItems: [
            ...toItems(delta.addDigitalSignageIds, 'digitalSignage'),
            ...toItems(delta.addGroupIds, 'group'),
            ...toItems(groupUiDeviceIds, 'digitalSignage'),
        ],
        removedItems: [
            ...toItems(delta.removeDigitalSignageIds, 'digitalSignage'),
            ...toItems(delta.removeGroupIds, 'group'),
        ],
    }
}

/** Правый список: устройства с слота + оверлей, без удалённых и без групп. */
export const mergeSidebarDevices = (
    boundDevices: IScheduleSidebarDevice[],
    overlayDevices: IScheduleSidebarDevice[],
    delta: IScheduleDeviceDelta
): IScheduleSidebarDevice[] => {
    const removed = new Set([...delta.removeDigitalSignageIds, ...delta.removeGroupIds])
    const fromBound = boundDevices.filter((device) => !removed.has(device.id) && !isGroupDevice(device))
    const boundIds = new Set(fromBound.map((device) => device.id))
    const fromOverlay = overlayDevices.filter(
        (device) => !boundIds.has(device.id) && !removed.has(device.id) && !isGroupDevice(device)
    )

    return [...fromBound, ...fromOverlay]
}
